<template>
  <template v-if="getCurrentApplication">
    <div class="mb-5">
      <ApplySubNav
        v-if="!isApplicantRequestPaymentFlow"
        active="income"
        :verify="getCurrentApplication.need_applicant_experian_authtoken"
        :income="getCurrentApplication.need_applicant_finicity_bank_connection"
        :pay="getCurrentApplication.price > 0"
      />
    </div>
    <div
      class="rounded-lg bg-white px-5 py-5 my-5 md:my-0 md:px-10 shadow-sm mx-auto max-w-95 lg:max-w-screen-md"
    >
      <h2 class="text-xlg font-semibold py-5 text-center">Automatically verify your income</h2>
      <div class="flex flex-row justify-center pb-3">
        <div class="flex items-center">
          <LockClosedIcon class="text-brand-blue w-6 h-6 mr-1" />
          <p class="text-brand-blue">Secure</p>
        </div>
      </div>

      <p class="py-2 mx-auto md:w-3/4">
        <template v-if="isApplicantRequestPaymentFlow">
          The property representative for {{ getCurrentApplication.unit_address }} has requested
          these additional reports. When you click below, you won't be charged until you finish the
          reports and submit payment with a credit card on a separate payment page.<br /><br />
        </template>
        <template v-else>
          The property representative for {{ getCurrentApplication.unit_address }} has requested
          that all applicants verify their income for this property. <br /><br />
        </template>
        We use Finicity to handle this automatically. Just login to your bank account, and they can
        estimate your income based on deposit history. Don't worry — we don't store any of your bank
        info, and nothing can be charged to your account.<br /><br />
        If you cant connect to Finicity, or don't feel comfortable, you can skip at the bottom of
        this page. However, applicants with verified income are 80% more likely to be accepted.
      </p>

      <div class="flex justify-center items-center py-10">
        <img class="w-auto h-10" src="@/assets/images/finicity_logo.png" />
        <PlusIcon class="w-7 h-7 my-3 mx-10" />
        <div class="flex items-center">
          <img src="@/assets/images/main-logo.svg" class="w-10 h-10" />
          <h5 class="ml-3 text-lg">Rent Application</h5>
        </div>
      </div>
      <div class="flex justify-center">
        <div class="flex flex-col justify-center w-2/5" style="">
          <p class="font-semibold py-1">Designed with your safety in mind</p>
          <p class="font-light py-1">
            Your data is powerful—and no one should access it without your permission. For that
            reason, our security practices are designed to meet or exceed industry standards.
          </p>
        </div>

        <div class="flex justify-center mx-2 py-2 w-1/10">
          <div class="vertical-line"></div>
        </div>

        <div class="flex justify-center w-2/5">
          <div class="flex flex-col justify-center">
            <div class="flex items-center">
              <CheckIcon class="text-brand-green mr-2 w-7 h-7" />
              <p>Data Encryption</p>
            </div>
            <div class="flex items-center">
              <CheckIcon class="text-brand-green mr-2 w-7 h-7" />
              <p>Strong Authentication</p>
            </div>
            <div class="flex items-center">
              <CheckIcon class="text-brand-green mr-2 w-7 h-7" />
              <p>Cloud Infrastructure</p>
            </div>
            <div class="flex items-center">
              <CheckIcon class="text-brand-green mr-2 w-7 h-7" />
              <p>Robust Monitoring</p>
            </div>
            <div class="flex items-center">
              <CheckIcon class="text-brand-green mr-2 w-7 h-7" />
              <p>Strong Authentication</p>
            </div>
            <router-link
              :to="{ name: 'home_security' }"
              class="text-brand-green hover:underline py-2"
              >Find out more</router-link
            >
          </div>
        </div>
      </div>

      <div class="w-1/2 md:w-1/4 mx-auto py-8">
        <PrimaryButton @click="linkBankAccount" :is-loading="isApplicationLoading">
          <div class="flex items-center justify-center py-1 md:py-0">
            <p class="text-white">Get Started</p>
            <ArrowRightIcon class="w-5 h-5 text-white ml-1" />
          </div>
        </PrimaryButton>
        <p
          class="text-brand-dark-gray hover:underline mt-5 cursor-pointer text-center"
          @click="toggleSkipModal(false)"
        >
          Skip
        </p>
      </div>
    </div>
  </template>

  <template v-if="finicityConnectUrl">
    <FinicityConnectModal
      :connectUrl="finicityConnectUrl"
      :showModal="showFinicityModal"
      @close="toggleFinicityModal"
    />
  </template>
  <SkipFinicityConnectModal :showModal="showSkipModal" @close="toggleSkipModal" />
</template>
<script>
import { PlusIcon, CheckIcon, LockClosedIcon, ArrowRightIcon } from "@heroicons/vue/outline"
import { mapGetters } from "vuex"
import { APPLICATION_GENERATE_FINICITY_CONNECT_URL } from "@/store/types"

import FinicityConnectModal from "./components/FinicityConnectModal.vue"
import SkipFinicityConnectModal from "./components/SkipFinicityConnectModal.vue"

export default {
  components: {
    PlusIcon,
    CheckIcon,
    LockClosedIcon,
    ArrowRightIcon,
    FinicityConnectModal,
    SkipFinicityConnectModal
  },
  computed: {
    ...mapGetters(["getCurrentApplication", "isApplicationLoading"]),
    currentStep() {
      switch (this.currentRouteName) {
        case "apply_verify_disclaimer":
          return 1
        case "apply_verify_personal_info":
          return 2
        case "apply_verify_questions":
          return 3
        default:
          return 1
      }
    },
    currentRouteName() {
      return this.$route.name
    },
    isApplicantRequestPaymentFlow() {
      return this.currentRouteName.includes("request_payment")
    }
  },
  data() {
    return {
      showFinicityModal: false,
      finicityConnectUrl: "",
      showSkipModal: false
    }
  },
  mounted() {
    if (
      this.getCurrentApplication &&
      !this.getCurrentApplication.need_applicant_finicity_bank_connection
    ) {
      if (this.getCurrentApplication.is_paid) {
        // if (!this.getCurrentApplication.applicant.affiliate_offer_optin) {
        //   this.$router.push({
        //     name: "apply_affiliate_optin",
        //     params: { id: this.getCurrentApplication.id }
        //   })
        // } else {
        //   this.$router.push({
        //     name: "apply_complete",
        //     params: { id: this.$route.params.id }
        //   })
        // }
        this.$router.push({
          name: "apply_complete",
          params: { id: this.$route.params.id }
        })
      } else {
        this.$router.push({
          name: "apply_pay",
          params: { id: this.$route.params.id }
        })
      }
    } else if (this.$route.name === "apply_verify") {
      this.$router.push({
        name: "apply_verify_disclaimer",
        params: { id: this.$route.params.id }
      })
    }
  },
  methods: {
    async linkBankAccount() {
      try {
        const response = await this.$store.dispatch(APPLICATION_GENERATE_FINICITY_CONNECT_URL, {
          id: this.$route.params.id
        })
        this.finicityConnectUrl = response.data.connect_url
        this.toggleFinicityModal()
      } catch (err) {
        console.log(err)
      }
    },
    toggleFinicityModal(next) {
      this.showFinicityModal = !this.showFinicityModal

      if (next) {
        if (this.isApplicantRequestPaymentFlow) {
          this.$router.push({
            name: "request_payment_pay"
          })
        } else {
          if (this.getCurrentApplication.is_paid) {
            // if (!this.getCurrentApplication.applicant.affiliate_offer_optin) {
            //   this.$router.push({
            //     name: "apply_affiliate_optin",
            //     params: { id: this.getCurrentApplication.id }
            //   })
            // } else {
            //   this.$router.push({
            //     name: "apply_complete",
            //     params: { id: this.$route.params.id }
            //   })
            // }

            this.$router.push({
              name: "apply_complete",
              params: { id: this.$route.params.id }
            })
          } else {
            this.$router.push({
              name: "apply_pay",
              params: { id: this.$route.params.id }
            })
          }
        }
      }
    },
    toggleSkipModal(next) {
      this.showSkipModal = !this.showSkipModal

      if (next) {
        if (this.isApplicantRequestPaymentFlow) {
          this.$router.push({
            name: "request_payment_pay"
          })
        } else {
          if (this.getCurrentApplication.is_paid) {
            if (!this.getCurrentApplication.applicant.affiliate_offer_optin) {
              this.$router.push({
                name: "apply_affiliate_optin",
                params: { id: this.getCurrentApplication.id }
              })
            } else {
              this.$router.push({
                name: "apply_complete",
                params: { id: this.$route.params.id }
              })
            }
          } else {
            this.$router.push({
              name: "apply_pay",
              params: { id: this.$route.params.id }
            })
          }
        }
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.vertical-line {
  border-left: 1px solid #cfcfd2;
  height: 100%;
}
</style>
